import React, { FC, useState } from 'react';
import {
    ActionList,
    AppHeader,
    AppSection,
    Button,
    Modal,
    ModalActions,
    ModalHeader,
    useSnackbar
} from '../../../components-v2/shared';
import { Filters } from '../../Filters/Filters.helpers';
import { BaseRow, Importer, ImporterField } from '@clxmedia/react-csv-importer';
import '@clxmedia/react-csv-importer/dist/index.css';
import { handlePromise } from '@lambdacurry/component-library';
import useStore from '../../../store/useStore';
import { v4 as uuidv4 } from 'uuid';
import DefaultRoutes from '../../../routes/DefaultRoutes';
import { sanitizePhoneNumber } from '../../../util/formatters';
import { DEFAULT_EMAIL_REGEX } from '../../../constants';

export const LeadImport: FC<{ filters?: Filters }> = () => {
    const { store } = useStore();
    const { Api, router } = store;
    const { addSnackbar } = useSnackbar();
    const [resultModal, setResultModal] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [progressPhase, setProgressPhase] = useState<'parsing' | 'uploading'>('parsing');
    const [rowsProcessed, setRowsProcessed] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [importResults, setImportResults] = useState<{ type: 'segment', payload: { id: number, name: string }, count: number } | { type: 'error', payload: string } | null>(null); // Single result object
    const [allRows, setAllRows] = useState<BaseRow[]>([]); // Accumulate rows across chunks

    async function doContactsUpload(rows: BaseRow[]) {
        // Filter rows with required fields
        const validRows = rows.map((row: { [key: string]: any }) => {
            const firstname = row.firstname?.trim();
            const lastname = row.lastname?.trim();

            const hasValidEmail = !!DEFAULT_EMAIL_REGEX.test(row.email?.trim().toLowerCase() || '');
            const sanitizedPhone = sanitizePhoneNumber(row.phone);

            // Leads must have first name, last name, and at least one contact method
            if (!firstname || !lastname || (!hasValidEmail && !sanitizedPhone)) {
                return null; // Skip invalid rows
            }

            return {
                firstname,
                lastname,
                // Use fallback <uuid>@noemail.com email if email is missing or invalid.
                // NOTE: This is currently necessary to pass backend validation for unique email addresses.
                email: hasValidEmail ? row.email?.trim() : `${uuidv4()}@noemail.com`,
                phone: sanitizedPhone,
                id: uuidv4(),
            };
        }).filter(row => row != null) as BaseRow[];

        setAllRows(prev => {
            const newRows = [...prev, ...validRows];
            setRowsProcessed(newRows.length); // Update progress
            return newRows;
        }); // Accumulate rows locally
    }

    async function finalizeImport() {
        if (allRows.length === 0) {
            addSnackbar('No valid data to import.', { variant: 'warning' });
            return;
        }

        setLoadingModal(true); // Show loading modal
        setProgressPhase('uploading'); // Switch to upload phase
        setTotalRows(allRows.length); // Set total for progress calculation

        const [response, error] = await handlePromise(
            Api.client.post(`company/${router.params.companyId}/contacts/import`, allRows)
        );

        setLoadingModal(false); // Hide loading modal

        if (!response?.data || error) {
            console.error(error?.response);
            addSnackbar('Failed to import leads.', { variant: 'error' });
            setAllRows([]); // Reset on failure
            return;
        }

        console.log('Import response:', response.data);

        if (response.data.type === 'segment') {
            router.goTo(
                DefaultRoutes.EditListSegmentPage,
                { companyId: router.params.companyId, listSegmentId: response.data.payload.id },
                store
            );
            return;
        }

        setImportResults(response.data); // Set the final result once
        setResultModal(true);
        setAllRows([]); // Reset after success

        // Reset progress tracking
        setRowsProcessed(0);
        setTotalRows(0);
        setProgressPhase('parsing');
    }

    return (
        <>
            <AppHeader title="Import Contacts" />
            <AppSection>
                <Importer
                    dataHandler={async rows => {
                        // Accumulate rows for each chunk
                        await doContactsUpload(rows);
                    }}
                    defaultNoHeader={false}
                    restartable={true}
                    onStart={() => {
                        setAllRows([]); // Reset rows at the start of a new import
                        setImportResults(null); // Clear previous results
                        setLoadingModal(true); // Show loading modal during parsing
                        setProgressPhase('parsing');
                        setRowsProcessed(0);
                        setTotalRows(0);
                    }}
                    onComplete={() => {
                        // Finalize the import after all chunks are processed
                        finalizeImport();
                    }}
                >
                    <ImporterField name="firstname" label="First Name" />
                    <ImporterField name="lastname" label="Last Name" />
                    <ImporterField name="email" label="Email" optional />
                    <ImporterField name="phone" label="Phone" optional />
                </Importer>
            </AppSection>

            {/* Loading Modal */}
            <Modal isOpen={loadingModal} onAfterClose={() => setLoadingModal(false)}>
                <ModalHeader title="Importing Records" />
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div style={{
                        display: 'inline-block',
                        width: '20px',
                        height: '20px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginRight: '10px'
                    }}></div>
                    <div>
                        {progressPhase === 'parsing' ? (
                            <span>
                                Parsing CSV file...
                                {rowsProcessed > 0 && (
                                    <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                                        {rowsProcessed} records processed
                                    </div>
                                )}
                            </span>
                        ) : (
                            <span>
                                Uploading {totalRows} records to server...
                                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                                    This may take a few moments
                                </div>
                            </span>
                        )}
                    </div>
                </div>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </Modal>            {/* Results Modal */}
            <Modal isOpen={resultModal} closeButton={false}>
                <ModalHeader title="Import Results" />
                {importResults?.type === 'segment' && (
                    <div>
                        <p>
                            <b>Records Queued For Import:</b> {importResults.count}
                        </p>
                        <p>
                            <b>New Segment Name:</b> {importResults.payload.name}
                        </p>
                        <p>
                            <Button
                                onClick={() => {
                                    // open the list segment in the current tab
                                    router.goTo(
                                        DefaultRoutes.EditListSegmentPage,
                                        { companyId: router.params.companyId, listSegmentId: importResults.payload.id },
                                        store
                                    );
                                }}
                            >
                                View New List Segment
                            </Button>
                        </p>
                        <p>
                            <b>Note:</b> It may take a few minutes for all contacts to appear in the system.
                        </p>
                    </div>
                )}
                {importResults?.type === 'error' && (
                    <div>
                        <p>
                            <b>Error during import:</b> {importResults.payload}
                        </p>
                    </div>
                )}
                <ModalActions>
                    <ActionList position="end">
                        <Button
                            onClick={() => {
                                setResultModal(false);
                                setImportResults(null); // Clear results on close
                            }}
                        >
                            Close
                        </Button>
                    </ActionList>
                </ModalActions>
            </Modal>
        </>
    );
};
